import { RotateCw } from 'lucide-react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { StateMark } from '@/components/brand/StateMark';
import { Button, SupportDetails, cn } from '@/components/ui';
import { errorDetails, reloadWithoutTabs } from '@/lib/errorReport';

/*
  ErrorBoundary — граница ошибок. React не умеет ловить ошибку отрисовки хуком: без границы любая ошибка
  в дереве размонтирует его целиком, и остаётся белый экран без объяснения.

  Границы две (R42):
  - на каждой вкладке (WorkspaceCanvas) — вкладка показывает «В этой вкладке сбой», остальное работает;
  - в корне (main.tsx) — запасной вид по умолчанию RootCrash: сцена «Интерфейс остановился из-за ошибки».
  onError — для записи в трассировку и отметки на вкладке; в консоль ошибка пишется всегда. resetKey — смена
  значения снимает сбой без перемонтирования: вкладка ушла со сбойного адреса — она снова целая, а при
  обычных переходах содержимое живёт как жило.

    <ErrorBoundary>
      <App />
    </ErrorBoundary>
*/

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, reset: () => void) => ReactNode;
  /** Ошибка поймана: запись в трассировку, отметка на вкладке. */
  onError?: (error: Error, info: ErrorInfo) => void;
  /** Сменился — сбой снят (адрес вкладки). */
  resetKey?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
  resetKey?: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { error: null, resetKey: this.props.resetKey };

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  static getDerivedStateFromProps(
    props: ErrorBoundaryProps,
    state: ErrorBoundaryState,
  ): Partial<ErrorBoundaryState> | null {
    return props.resetKey !== state.resetKey ? { error: null, resetKey: props.resetKey } : null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Поймана ошибка:', error, info.componentStack);
    // Отправка в трассировку — зона ядра (spec-2 §11).
    this.props.onError?.(error, info);
  }

  reset = () => {
    this.setState({ error: null });
  };

  override render() {
    const { error } = this.state;
    if (error) {
      return this.props.fallback ? (
        this.props.fallback(error, this.reset)
      ) : (
        <RootCrash error={error} onReset={this.reset} />
      );
    }
    return this.props.children;
  }
}

/** Текст падения — один для сцены и для запасного текстового вида. */
const CRASH_TITLE = 'Интерфейс остановился из-за ошибки';
const CRASH_TEXT = 'Данные не пострадали. Обычно помогает перезагрузка — вкладки восстановятся.';

/**
 * RootCrash — падение всего интерфейса (R42, spec-2 §3.7). Оболочки уже нет, поэтому сцена тёмная, без
 * провайдеров — только классы и Button: знак с живым ядром (веер цел, ядро краснеет скачком, одна тревога,
 * дальше ядро остановилось), что случилось и что делать. Прокрутка своя (R28).
 * Внутри — вторая граница: упадёт сам знак или кнопка — останется чисто текстовый вид.
 * contained — для галереи: сцена внутри рамки, без голоса.
 */
export function RootCrash({
  error,
  onReset,
  contained = false,
}: {
  error: Error;
  onReset: () => void;
  contained?: boolean;
}) {
  return (
    <div
      data-theme="dark"
      data-slot="root-crash"
      className={cn('overflow-y-auto bg-night text-petal', contained ? 'absolute inset-0' : 'h-dvh')}
    >
      <ErrorBoundary fallback={() => <PlainCrash error={error} />}>
        <CrashScene error={error} onReset={onReset} contained={contained} />
      </ErrorBoundary>
    </div>
  );
}

function CrashScene({
  error,
  onReset,
  contained,
}: {
  error: Error;
  onReset: () => void;
  contained: boolean;
}) {
  return (
    <div className="flex min-h-full flex-col items-center px-6 py-16 text-center">
      {/* Центр — распорками: верхняя здесь, нижняя — раскрывашка подробностей (раскрытая, растёт вниз). */}
      <div aria-hidden className="grow basis-0" />
      <StateMark kind="crashed" setting="scene" className="size-24" />
      <h1 className="mt-8 text-xl font-semibold text-petal">{CRASH_TITLE}</h1>
      <p className="mt-2 max-w-md text-sm text-petal/70">{CRASH_TEXT}</p>
      <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
        <Button variant="primary" leftIcon={<RotateCw />} onClick={() => window.location.reload()}>
          Перезагрузить
        </Button>
        <Button variant="secondary" onClick={onReset}>
          Попробовать снова
        </Button>
      </div>
      <Button variant="link" size="sm" className="mt-4" onClick={reloadWithoutTabs}>
        Открыть без восстановления вкладок
      </Button>
      <SupportDetails text={errorDetails(error)} />
      {/* Голос — один раз, при появлении сцены. */}
      {!contained && (
        <p role="alert" className="sr-only">
          {CRASH_TITLE}. {CRASH_TEXT}
        </p>
      )}
    </div>
  );
}

/** Запасной вид, если упала сама сцена: только текст и простые кнопки. */
function PlainCrash({ error }: { error: Error }) {
  return (
    <div
      role="alert"
      className="flex min-h-full flex-col items-center justify-center gap-3 px-6 py-16 text-center"
    >
      <h1 className="text-xl font-semibold text-petal">{CRASH_TITLE}</h1>
      <p className="max-w-md text-sm text-petal/70">{CRASH_TEXT}</p>
      <p className="flex flex-wrap justify-center gap-4 text-sm">
        <button
          type="button"
          className="underline underline-offset-4"
          onClick={() => window.location.reload()}
        >
          Перезагрузить
        </button>
        <button type="button" className="underline underline-offset-4" onClick={reloadWithoutTabs}>
          Открыть без восстановления вкладок
        </button>
      </p>
      <pre className="max-w-md font-mono text-2xs break-all whitespace-pre-wrap text-petal/70">
        {errorDetails(error)}
      </pre>
    </div>
  );
}
